/**
 * ForgotPassword — Phase 2 (Executive Redesign, Entry Experience).
 *
 * Presentation migrated to `@/design-system` primitives + `--ds-*` tokens.
 * Test IDs, submit flow, rate-limit copy, and success-state routing all
 * preserved.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import AuthShell from "./AuthShell";
import { Button, Input, Card, CardBody } from "@/design-system";
import { FormField } from "./Login";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  return (
    <AuthShell
      testId="forgot-password-page"
      title={sent ? "Check your inbox." : "Reset your password."}
      subtitle={
        sent
          ? undefined
          : "Enter the email associated with your account and we'll send you a reset link."
      }
    >
      {sent ? (
        <Card data-testid="forgot-password-sent">
          <CardBody className="p-6 md:p-8">
            <Mail
              className="w-7 h-7 mb-3"
              style={{ color: "var(--ds-color-accent)" }}
              aria-hidden
            />
            <p className="text-[15px] leading-relaxed mb-4 text-[var(--ds-color-ink-muted)]">
              If an account exists for{" "}
              <strong className="text-[var(--ds-color-ink)]">{email}</strong>, we
              just sent a password reset link. The link expires in 1 hour.
            </p>
            <p className="text-[13px] leading-snug text-[var(--ds-color-ink-subtle)]">
              Didn't receive it? Check your spam folder, or wait a minute and try again.
              We rate-limit reset requests to 3 per hour per email.
            </p>
            <div className="mt-6">
              <Link
                to="/login"
                data-testid="forgot-return-login"
                className="text-[13px] font-medium text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)] transition-colors"
              >
                Return to sign in
              </Link>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody className="p-6 md:p-8">
            <form onSubmit={submit} className="space-y-4">
              <FormField label="Email" htmlFor="forgot-email-input">
                <Input
                  id="forgot-email-input"
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </FormField>
              <Button
                data-testid="forgot-submit"
                type="submit"
                loading={busy}
                disabled={busy || !email.trim()}
                className="w-full"
                size="lg"
              >
                {busy ? "Sending…" : "Send reset link"}
              </Button>
              <p className="text-[13px] text-center leading-snug text-[var(--ds-color-ink-subtle)]">
                For security, we send the same response whether or not the email exists.
              </p>
              <div className="text-[13px] text-center pt-2 border-t border-[var(--ds-color-border)] text-[var(--ds-color-ink-muted)]">
                Remember it?{" "}
                <Link
                  to="/login"
                  data-testid="forgot-back-to-login"
                  className="font-medium text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)] transition-colors"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          </CardBody>
        </Card>
      )}
    </AuthShell>
  );
}
